import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import FilmStripLoader from '../components/FilmStripLoader';
import { getYouTubeEmbedUrl } from '../utils/youtubeEmbed';

const SeriesDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useAuth();

  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);

  const [selectedSeason, setSelectedSeason] = useState(null);

  const [showTrailer, setShowTrailer] = useState(false);
  const [trailerUrl, setTrailerUrl] = useState('');

  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoEmbedUrl, setVideoEmbedUrl] = useState('');
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [currentEpisode, setCurrentEpisode] = useState(null);

  const castRef = useRef(null);
  const videoIframeRef = useRef(null);
  const videoContainerRef = useRef(null);

  // =========================
  // Reviews (Series)
  // =========================
  const [reviews, setReviews] = useState([]);
  const [reviewsSlide, setReviewsSlide] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });

  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewMessage, setReviewMessage] = useState('');

  const authHeaders = useMemo(() => {
    // ndrysho key nëse ti e ruan ndryshe token-in
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchSeries = async () => {
    try {
      const res = await axios.get(`/api/series/${id}`);
      setSeries(res.data);
      return res.data;
    } catch (error) {
      console.error('Error fetching series:', error);
      throw error;
    }
  };

  const fetchReviews = async (seriesId) => {
    try {
      const res = await axios.get(`/api/reviews/series/${seriesId}`, {
        headers: { ...authHeaders },
      });
      setReviews(res.data || []);
      setReviewsSlide(0);
    } catch (err) {
      // Silently fail - reviews endpoint doesn't exist for series yet
      setReviews([]);
    }
  };

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        // First fetch series, then fetch reviews with the series ID
        const seriesData = await fetchSeries();
        if (seriesData?._id) {
          await fetchReviews(seriesData._id);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        // Small delay to ensure everything is rendered before hiding loader
        setTimeout(() => {
          setLoading(false);
          // Start fade-out animation, then hide loader after animation completes
          setTimeout(() => {
            setShowLoader(false);
          }, 600);
        }, 500);
      }
    };
    
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (series?.seasons?.length > 0 && !selectedSeason) {
      setSelectedSeason(series.seasons[0]);
    }
  }, [series, selectedSeason]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showTrailer || showVideoPlayer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTrailer, showVideoPlayer]);

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!series?._id) return;

    setReviewLoading(true);
    setReviewError('');
    setReviewMessage('');

    try {
      // Shumica e backend-eve kërkojnë login për reviews
      // nëse ti s’je logged in, të çon te login
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        setReviewError('Please login to add a review.');
        return;
      }

      await axios.post(
        '/api/reviews',
        {
          seriesId: series._id,
          rating: Number(reviewForm.rating),
          comment: reviewForm.comment,
        },
        { headers: { ...authHeaders } }
      );

      setReviewMessage('Avis soumis avec succès.');
      setReviewForm({ rating: 5, comment: '' });
      setShowReviewForm(false);

      await fetchReviews(series._id);
    } catch (err) {
      // Silently handle review errors - reviews endpoint may not exist for series
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (Array.isArray(err?.response?.data?.errors) ? err.response.data.errors[0]?.msg : null) ||
        'Échec de la soumission de l\'avis.';

      setReviewError(msg);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleWatchEpisode = async (seasonNumber, episodeNumber) => {
    if (!hasActiveSubscription) {
      navigate('/subscriptions');
      return;
    }

    setLoadingVideo(true);
    setVideoError(null);
    setShowVideoPlayer(true);
    setCurrentEpisode({ seasonNumber, episodeNumber });

    try {
      // Get video token
      const tokenRes = await axios.get(
        `/api/series/${id}/season/${seasonNumber}/episode/${episodeNumber}/watch`
      );
      const token = tokenRes.data.token;

      // Get embed URL from backend
      const embedRes = await axios.get(`/api/stream/${token}`);
      
      if (embedRes.data.success && embedRes.data.embedUrl) {
        setVideoEmbedUrl(embedRes.data.embedUrl);
        
        // Save watch history
        try {
          await axios.post(`/api/series/${id}/season/${seasonNumber}/episode/${episodeNumber}/history`, { timestamp: 0 });
        } catch (err) {
          console.error('Error saving history:', err);
        }
      } else {
        setVideoError('Échec du chargement de la vidéo. Veuillez réessayer.');
        setShowVideoPlayer(false);
      }
    } catch (error) {
      console.error('Error loading episode:', error);
      if (error.response?.status === 403) {
        setVideoError('You need an active subscription to watch this episode.');
      } else {
        setVideoError(error.response?.data?.message || 'Failed to load video. Please try again.');
      }
      setShowVideoPlayer(false);
    } finally {
      setLoadingVideo(false);
    }
  };

  const handleStartWatching = async () => {
    if (!hasActiveSubscription) {
      navigate('/subscriptions');
      return;
    }

    if (!series?.seasons?.length) {
      alert('No episodes available');
      return;
    }

    try {
      let seasonNumber = 1;
      let episodeNumber = 1;

      // Check watch history to continue from last watched episode
      try {
        const historyRes = await axios.get('/api/users/history');
        const seriesHistory = (historyRes.data || []).filter(
          (h) => h.seriesId && (h.seriesId._id === id || h.seriesId === id)
        );

        if (seriesHistory.length > 0) {
          const lastWatched = seriesHistory.sort((a, b) => {
            const dateA = new Date(a.watchedAt || 0);
            const dateB = new Date(b.watchedAt || 0);
            return dateB - dateA;
          })[0];

          seasonNumber = lastWatched.seasonNumber || 1;
          episodeNumber = lastWatched.episodeNumber || 1;
        }
      } catch (error) {
        console.error('Error fetching history:', error);
      }

      // Validate season and episode exist
      const season = series.seasons.find((s) => s.seasonNumber === seasonNumber);
      if (!season?.episodes?.length) {
        seasonNumber = series.seasons[0].seasonNumber;
        episodeNumber = series.seasons[0].episodes[0]?.episodeNumber || 1;
      } else {
        const episode = season.episodes.find((e) => e.episodeNumber === episodeNumber);
        if (!episode) episodeNumber = season.episodes[0]?.episodeNumber || 1;
      }

      // Open video player modal instead of navigating
      await handleWatchEpisode(seasonNumber, episodeNumber);
    } catch (error) {
      console.error('Error starting series:', error);
      alert(error.response?.data?.message || 'Échec du démarrage de la lecture');
    }
  };

  const StarRating = ({ value, sizeClass = 'w-5 h-5', idPrefix = 'star' }) => {
    const fullStars = Math.floor(value);
    const hasHalf = value % 1 >= 0.5;

    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => {
          if (i < fullStars) {
            return (
              <svg
                key={i}
                className={`${sizeClass} text-white`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            );
          }

          if (i === fullStars && hasHalf) {
            const gradId = `${idPrefix}-half-${i}`;
            return (
              <svg key={i} className={sizeClass} viewBox="0 0 20 20">
                <defs>
                  <linearGradient id={gradId}>
                    <stop offset="50%" stopColor="white" />
                    <stop offset="50%" stopColor="#555" />
                  </linearGradient>
                </defs>
                <path
                  fill={`url(#${gradId})`}
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            );
          }

          return (
            <svg
              key={i}
              className={`${sizeClass} text-[#555]`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          );
        })}
      </div>
    );
  };

  if (showLoader) {
    return (
      <div className={loading ? '' : 'fade-out-loader'}>
        <FilmStripLoader />
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-[#141414]">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <p className="text-white">Série non trouvée</p>
        </div>
      </div>
    );
  }

  // Reviews data (slider)
  const perView = 2;
  const totalPages = Math.max(1, Math.ceil((reviews?.length || 0) / perView));
  const clampedSlide = Math.min(reviewsSlide, totalPages - 1);
  const prev = () => setReviewsSlide((s) => Math.max(0, s - 1));
  const next = () => setReviewsSlide((s) => Math.min(totalPages - 1, s + 1));

  return (
    <div className="min-h-screen bg-[#141414]">
      <Header />

      {/* Hero */}
      <div className="relative h-[70vh] my-12 mx-10 md:mx-20">
        <div className="absolute inset-0 rounded-3xl overflow-hidden bg-[#141414]">
          <div
            className="absolute inset-0 bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${series.poster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-black/70" />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, #141414 0%, #141414cc 40%, #14141466 65%, #14141400 100%)',
            }}
          />
        </div>

        <div className="relative container mx-auto px-4 h-full flex items-end justify-center pb-24">
          <div className="max-w-3xl text-center">
            <h1 className="text-5xl font-bold text-white mb-4">{series.title}</h1>
            <p className="text-gray-300 mb-6 line-clamp-3">{series.description}</p>

            <div className="flex items-center justify-center gap-3 flex-wrap">
              {hasActiveSubscription ? (
                <button
                  onClick={handleStartWatching}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  <span>Commencer à regarder</span>
                </button>
              ) : (
                <Link
                  to="/subscriptions"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  S'abonner pour regarder
                </Link>
              )}

              {series.trailer && (
                <button
                  onClick={() => {
                    setTrailerUrl(series.trailer);
                    setShowTrailer(true);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900/60 text-white rounded-lg hover:bg-gray-900/80 transition font-semibold"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  <span>Voir la bande-annonce</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Seasons & Episodes + Sidebar */}
      <div className="container mx-auto px-4 pb-12">
        {series.seasons && series.seasons.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Saisons & Épisodes</h2>

            <div className="flex flex-wrap gap-2 mb-6">
              {series.seasons.map((season) => (
                <button
                  key={season.seasonNumber}
                  onClick={() => setSelectedSeason(season)}
                  className={`px-4 py-2 rounded-lg transition ${
                    selectedSeason?.seasonNumber === season.seasonNumber
                      ? 'bg-white text-gray-900 font-semibold'
                      : 'bg-[#0F0F0F] border-2 border-[#262626] text-white hover:bg-white hover:text-black'
                  }`}
                >
                  Saison {season.seasonNumber}
                </button>
              ))}
            </div>

            {selectedSeason && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <div className="space-y-2 bg-[#1A1A1A] p-7 rounded-2xl border border-[#262626]">
                    {selectedSeason.episodes.map((episode) => (
                      <div
                        key={episode.episodeNumber}
                        className="bg-[#0F0F0F] border border-[#262626] gap-5 md:gap-0 flex-col md:flex-row rounded-xl p-4 flex items-center justify-between hover:bg-[#141414] transition"
                      >
                        <div className="flex items-center space-x-4 flex-1 flex-col md:flex-row gap-4 md:gap-0 text-center md:text-start">
                          <div className="w-32 h-20 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                            {episode.thumbnail ? (
                              <img
                                src={episode.thumbnail}
                                alt={episode.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                E{episode.episodeNumber}
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <h3 className="text-white font-semibold mb-1">
                              Épisode {episode.episodeNumber} : {episode.title}
                            </h3>

                            {episode.description && (
                              <p className="text-gray-400 text-sm line-clamp-2">
                                {episode.description}
                              </p>
                            )}

                            {episode.duration && (
                              <p className="text-gray-500 text-xs mt-1">
                                {episode.duration} min
                              </p>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() =>
                            handleWatchEpisode(selectedSeason.seasonNumber, episode.episodeNumber)
                          }
                          className="px-6 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold ml-4 shrink-0"
                        >
                          {hasActiveSubscription ? 'Regarder' : 'S\'abonner'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-[#262626] space-y-10">
                    <div>
                      <div className="flex items-center gap-2 text-[#999999] mb-3">
                        <span className="font-medium">Année de sortie</span>
                      </div>
                      <div className="text-white text-lg font-semibold">
                        {series.releaseYear || '-'}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-[#999999] mb-4">
                        <span className="font-medium">Langues disponibles</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {(series.language?.length ? series.language : ['English']).map((lang, i) => (
                          <span
                            key={i}
                            className="px-4 py-2 rounded-xl bg-[#141414] border border-[#262626] text-white text-sm"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-[#999999] mb-4">
                        <span className="font-medium">Note</span>
                      </div>

                      <div className="bg-[#0F0F0F] border border-[#262626] rounded-2xl p-4">
                        <div className="text-white text-base sm:text-lg font-semibold mb-3">H4KIG STREAM</div>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <StarRating
                            value={series.rating?.site || 0}
                            sizeClass="w-5 h-5 sm:w-6 sm:h-6"
                            idPrefix="site"
                          />
                          <span className="text-white text-lg sm:text-xl font-semibold">
                            {(series.rating?.site || 0).toFixed(1).replace('.0', '')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-[#999999] mb-4">
                        <span className="font-medium">Genres</span>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        {(series.genre || []).map((g, i) => (
                          <span
                            key={i}
                            className="px-4 py-2 rounded-xl bg-[#141414] border border-[#262626] text-white text-sm"
                          >
                            {g}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-[#999999] font-medium mb-3">Réalisateur</div>
                      <div className="flex items-center gap-4 bg-[#0F0F0F] border border-[#262626] rounded-2xl p-4">
                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#2A2A2A] shrink-0">
                          {series.directorImage ? (
                            <img
                              src={series.directorImage}
                              alt={series.director}
                              className="w-full h-full object-cover"
                              onError={(e) => (e.currentTarget.style.display = 'none')}
                            />
                          ) : null}
                        </div>
                        <div className="min-w-0">
                          <div className="text-white font-semibold truncate">
                            {series.director || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}
      </div>

      {/* Description + Cast + Reviews */}
      <div className="container mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mb-8 bg-[#1A1A1A] p-10 rounded-xl border border-[#262626]">
              <h2 className="text-[#999999] text-lg mb-4">Description</h2>
              <p className="text-gray-300">{series.description}</p>
            </div>

            {series.cast && series.cast.length > 0 && (
              <div className="relative mb-8 bg-[#1A1A1A] p-8 rounded-2xl border border-[#262626]">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-[#999999] text-lg font-medium">Casting</h2>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => castRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
                      className="w-11 h-11 rounded-full bg-black/40 hover:bg-black/60 transition flex items-center justify-center text-white border border-[#262626]"
                      aria-label="Previous"
                    >
                      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={() => castRef.current?.scrollBy({ left: 320, behavior: 'smooth' })}
                      className="w-11 h-11 rounded-full bg-black/40 hover:bg-black/60 transition flex items-center justify-center text-white border border-[#262626]"
                      aria-label="Next"
                    >
                      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div ref={castRef} className="flex gap-6 overflow-hidden scroll-smooth select-none" onWheel={(e) => e.preventDefault()}>
                  {series.cast.map((actor, index) => {
                    const actorName = typeof actor === 'string' ? actor : actor?.name;
                    const actorImage = typeof actor === 'object' ? actor?.image : null;

                    return (
                      <div key={index} className="flex-shrink-0">
                        <div className="rounded-2xl w-[102px] h-[100px] overflow-hidden bg-[#2A2A2A]">
                          {actorImage ? (
                            <img
                              src={actorImage}
                              alt={actorName}
                              className="w-full h-full object-cover pointer-events-none"
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-[#333]" />
                          )}
                        </div>

                        <p className="text-white text-sm font-medium mt-3 truncate text-center w-[102px]">
                          {actorName}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Reviews */}
            <div className="bg-[#1A1A1A] p-10 rounded-2xl border border-[#262626]">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-[#999999] text-lg font-medium">Avis</h2>

                <button
                  onClick={() => setShowReviewForm((v) => !v)}
                  className="inline-flex items-center gap-3 px-6 py-3 bg-black/30 text-white rounded-xl border border-[#2A2A2A] hover:bg-black/50 transition"
                >
                  <span className="text-2xl leading-none">+</span>
                  <span className="font-medium">Ajouter votre avis</span>
                </button>
              </div>

              {reviewError && (
                <div className="mb-6 p-4 rounded-xl border border-red-700 bg-red-900/30 text-red-200">
                  {reviewError}
                </div>
              )}
              {reviewMessage && (
                <div className="mb-6 p-4 rounded-xl border border-green-700 bg-green-900/30 text-green-200">
                  {reviewMessage}
                </div>
              )}

              {showReviewForm && (
                <form
                  onSubmit={handleAddReview}
                  className="mb-10 p-6 bg-[#141414] rounded-2xl border border-[#262626]"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white mb-2">Rating</label>
                      <select
                        value={reviewForm.rating}
                        onChange={(e) =>
                          setReviewForm({ ...reviewForm, rating: parseInt(e.target.value, 10) })
                        }
                        className="w-full px-4 py-3 bg-[#101010] text-white rounded-xl border border-[#262626]"
                      >
                        {[1, 2, 3, 4, 5].map((r) => (
                          <option key={r} value={r}>
                            {r} Étoile{r > 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-white mb-2">Commentaire</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        className="w-full px-4 py-3 bg-[#101010] text-white rounded-xl border border-[#262626]"
                        rows="4"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={reviewLoading}
                        className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-100 transition font-semibold disabled:opacity-50"
                      >
                        {reviewLoading ? 'Envoi...' : 'Soumettre l\'avis'}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {reviews.length > 0 ? (
                <div className="relative">
                  <div className="overflow-hidden">
                    <div
                      className="flex transition-transform duration-500 ease-out"
                      style={{ transform: `translateX(-${clampedSlide * 100}%)` }}
                    >
                      {Array.from({ length: totalPages }).map((_, pageIdx) => {
                        const start = pageIdx * perView;
                        const pageItems = reviews.slice(start, start + perView);

                        return (
                          <div key={pageIdx} className="w-full flex-shrink-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              {pageItems.map((r) => (
                                <div
                                  key={r._id}
                                  className="bg-[#0F0F0F] rounded-2xl border border-[#262626] p-8 min-h-[260px]"
                                >
                                  <div className="flex items-start justify-between gap-4 mb-6">
                                    <div>
                                      <div className="text-white text-2xl font-semibold">
                                        {r.username || 'Utilisateur'}
                                      </div>
                                      <div className="text-[#7C7C7C] mt-1">Utilisateur</div>
                                    </div>

                                    {r.rating != null && (
                                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 border border-[#262626]">
                                        <div className="flex items-center">
                                          {Array.from({ length: 5 }).map((__, sIdx) => (
                                            <svg
                                              key={sIdx}
                                              className={`w-4 h-4 ${
                                                sIdx < Math.round(r.rating)
                                                  ? 'text-white'
                                                  : 'text-white/25'
                                              }`}
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.783.57-1.838-.197-1.539-1.118l1.071-3.292a1 1 0 00-.364-1.118L2.98 8.709c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                          ))}
                                        </div>
                                        <span className="text-white/80">
                                          {Number(r.rating).toFixed(1).replace('.0', '')}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  <p className="text-[#9A9A9A] leading-relaxed line-clamp-5">
                                    {r.comment || ''}
                                  </p>
                                </div>
                              ))}

                              {pageItems.length === 1 && <div className="hidden md:block bg-transparent" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {totalPages > 1 && (
                    <div className="mt-10 flex items-center justify-center gap-6">
                      <button
                        type="button"
                        onClick={prev}
                        disabled={clampedSlide === 0}
                        className={`w-12 h-12 rounded-full border border-[#262626] flex items-center justify-center transition
                          ${clampedSlide === 0 ? 'bg-black/20 text-white/30' : 'bg-black/35 hover:bg-black/55 text-white'}`}
                        aria-label="Previous"
                      >
                        <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>

                      <div className="flex items-center gap-2">
                        {Array.from({ length: totalPages }).map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setReviewsSlide(idx)}
                            className={`h-1.5 rounded-full transition ${
                              idx === clampedSlide ? 'bg-white w-10' : 'bg-white/20 w-5'
                            }`}
                            aria-label={`Go to page ${idx + 1}`}
                            type="button"
                          />
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={next}
                        disabled={clampedSlide === totalPages - 1}
                        className={`w-12 h-12 rounded-full border border-[#262626] flex items-center justify-center transition
                          ${clampedSlide === totalPages - 1 ? 'bg-black/20 text-white/30' : 'bg-black/35 hover:bg-black/55 text-white'}`}
                        aria-label="Next"
                      >
                        <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-400">Aucun avis pour le moment. Soyez le premier à donner votre avis !</p>
              )}
            </div>
          </div>

          {/* right column empty - e le për më vonë */}
          <div className="lg:col-span-1" />
        </div>
      </div>

      {/* Trailer Modal */}
      {showTrailer && series?.trailer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowTrailer(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-gray-300 transition z-10 font-bold"
              style={{ fontSize: '2rem', zIndex: 10000 }}
            >
              ✕
            </button>
            <div className="relative" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={getYouTubeEmbedUrl(trailerUrl)}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                playsInline
                webkit-playsinline="true"
                loading="eager"
                title="Trailer"
                style={{ touchAction: 'none' }}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {showVideoPlayer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex items-center justify-center p-4"
          onClick={() => {
            // Exit fullscreen if active
            if (document.fullscreenElement || 
                document.webkitFullscreenElement || 
                document.mozFullScreenElement || 
                document.msFullscreenElement) {
              if (document.exitFullscreen) {
                document.exitFullscreen();
              } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
              } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
              } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
              }
            }
            // Clean up
            setShowVideoPlayer(false);
            setVideoEmbedUrl('');
            setVideoError(null);
            setLoadingVideo(false);
            setCurrentEpisode(null);
          }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                // Exit fullscreen if active
                if (document.fullscreenElement || 
                    document.webkitFullscreenElement || 
                    document.mozFullScreenElement || 
                    document.msFullscreenElement) {
                  if (document.exitFullscreen) {
                    document.exitFullscreen();
                  } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                  } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                  } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                  }
                }
                // Clean up
                setShowVideoPlayer(false);
                setVideoEmbedUrl('');
                setVideoError(null);
                setLoadingVideo(false);
                setCurrentEpisode(null);
              }}
              className="absolute -top-12 right-0 text-white text-3xl hover:text-gray-300 transition z-10 font-bold"
              style={{ fontSize: '2rem', zIndex: 10000 }}
            >
              ✕
            </button>

            {loadingVideo ? (
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-xl">Loading video...</div>
                </div>
              </div>
            ) : videoEmbedUrl ? (
              <div 
                ref={videoContainerRef}
                className="relative" 
                style={{ paddingBottom: '56.25%' }}
              >
                <iframe
                  ref={videoIframeRef}
                  key={videoEmbedUrl}
                  src={videoEmbedUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  playsInline
                  webkit-playsinline="true"
                  loading="eager"
                  title="Episode Player"
                  style={{ touchAction: 'none' }}
                  onLoad={() => {
                    console.log('Video player iframe loaded successfully');
                    setVideoError(null);
                  }}
                  onError={(e) => {
                    console.error('Video player iframe error:', e);
                    setVideoError('Échec du chargement du lecteur vidéo. Veuillez réessayer.');
                  }}
                />
              </div>
            ) : videoError ? (
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <div className="text-white text-xl text-center px-4">{videoError}</div>
                  <button
                    onClick={() => currentEpisode && handleWatchEpisode(currentEpisode.seasonNumber, currentEpisode.episodeNumber)}
                    className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-xl">Échec du chargement de la vidéo</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SeriesDetail;
